// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import DatePickerPresentationPropsDefault from "@insite/mobius/DatePicker/presentationProps";
import { IconWrapper } from "@insite/mobius/Icon";
import getColor from "@insite/mobius/utilities/getColor";
import getContrastColor from "@insite/mobius/utilities/getContrastColor";
import Calendar from "@synd/XDL/Icons/Calendar";
import Close from "@synd/XDL/Icons/Close";
import Next from "@synd/XDL/Icons/LinkArrowRight";
import Previous from "@synd/XDL/Icons/LinkLeftArrow";
import { placeholderStyle } from "@synd/XDL/PreStyle/formField";
import { fontConfig } from "@synd/XDL/PreStyle/typography";
import { xBaseTheme } from "@synd/XDL/types";
import color from "@synd/XDL/Utilities/color";
import React from "react";
import { css } from "styled-components";

export default {
    defaultProps: {
        mergeCss: true,
        ...DatePickerPresentationPropsDefault,
        dateTimePickerProps: {
            calendarProps: {
                nextLabel: React.createElement(Next),
                prevLabel: React.createElement(Previous),
            } as any,
        },
        calendarIconProps: {
            ...DatePickerPresentationPropsDefault?.calendarIconProps,
            src: Calendar,
            size: 16,
            width: 14,
            color: "secondary",
            css: css`
                ${DatePickerPresentationPropsDefault?.calendarIconProps?.css}
                && {
                    padding: 9px 0;
                    margin-right: 0;
                }
                &:hover {
                    color: ${color("common.ui10")};
                }
            `,
        },
        clearIconProps: {
            ...DatePickerPresentationPropsDefault?.clearIconProps,
            src: Close,
            size: 10,
            color: "secondary",
            css: css`
                ${DatePickerPresentationPropsDefault?.clearIconProps?.css}
                && {
                    padding: 0;
                }
                &:hover {
                    color: ${color("common.ui10")};
                }
            `,
        },
        placeholders: {
            dayPlaceholder: "DD",
            monthPlaceholder: "MM",
            yearPlaceholder: "YYYY",
        },
        cssOverrides: {
            datePicker: css`
                line-height: 16px;
                .react-calendar {
                    /* right: unset;
                    left: 0; */

                    &__month-view {
                        &__weekdays {
                            font-size: ${fontConfig.small.size};
                            font-weight: ${fontConfig.weight.bold};
                            color: ${getColor("secondary")};
                            text-align: center;
                            abbr {
                                text-decoration: none;
                            }
                        }
                    }
                    .react-calendar__navigation {
                        &__arrow {
                            height: auto;
                        }

                        &__prev-button,
                        &__next-button {
                            color: ${getColor("text")};
                            background-image: none;
                            background: transparent;
                            &:enabled {
                                &:focus:not(:disabled) {
                                    background: transparent;
                                    color: ${getColor("common.ui10")};
                                }

                                &:hover:not(:disabled) {
                                    background: transparent;
                                    color: ${getColor("common.ui10")};
                                }
                            }

                            &:disabled {
                                opacity: 0.33;
                                cursor: not-allowed;
                            }

                            svg {
                                width: 8px;
                                height: 14px;
                            }
                        }

                        &__label {
                            &:enabled {
                                &:hover:not(:disabled),
                                &:focus:not(:disabled) {
                                    background: transparent;
                                }
                                &:focus:not(:disabled) {
                                    color: ${getColor("text")};
                                    outline: none;
                                }

                                &:hover:not(:disabled) {
                                    color: ${getColor("common.ui10")};
                                }
                            }
                        }
                    }

                    button {
                        background-color: ${getColor("common.background")};
                        &:enabled {
                            &:hover:not(:disabled),
                            &:focus:not(:disabled) {
                                color: ${getColor("common.white")};
                                background-color: ${getColor("common.ui10")};
                            }
                        }

                        &:disabled {
                            &,
                            &:hover,
                            &:focus {
                                color: ${getColor("common.border")};
                                cursor: default;
                            }
                        }
                    }
                    &__tile {
                        &--hasActive:not(:disabled):not(.react-calendar__month-view__days__day--neighboringMonth),
                        &--active:not(:disabled):not(.react-calendar__month-view__days__day--neighboringMonth) {
                            background-color: ${getColor("common.ui11")};
                            &:hover {
                                color: ${({ theme }) => getContrastColor("secondary", theme)};
                            }
                        }
                    }
                }
                .react-datetime-picker {
                    box-shadow: inset 0 1px 0 0 ${color("common.ui7", 0.1)};
                    color: ${color("common.ui7")};
                    min-height: 36px;
                    &:not(:disabled) {
                        &:not(:focus-within):hover {
                            border-color: ${color("common.ui10")};
                        }
                        &:focus-within {
                            border-color: ${color("common.ui11")};
                        }
                    }
                    &:not(:disabled),
                    &:focus-within {
                        padding: 0 12px 0 12px;
                        .react-datetime-picker__inputGroup {
                            padding: 0 12px 0 0;
                        }
                        .react-datetime-picker__button {
                            padding-right: 0;
                            display: flex;
                            align-items: center;
                        }
                    }

                    &__inputGroup {
                        input::placeholder {
                            ${placeholderStyle}
                        }

                        &__input {
                            box-shadow: none;
                            ${placeholderStyle}
                            padding: 0;
                            text-align: center;

                            &:not(:placeholder-shown) + span {
                                color: ${color("common.black")};
                                font-style: normal;
                            }
                        }

                        &__day:placeholder-shown {
                            // XNG-Changes: increase width to avoid the cuttoff due to the italic style
                            width: 22px !important;
                        }
                        &__month:placeholder-shown {
                            // XNG-Changes: increase width to avoid the cuttoff due to the italic style
                            width: 27px !important;
                        }
                        &__year:placeholder-shown {
                            // XNG-Changes: increase width to avoid the cuttoff due to the italic style
                            width: 36px !important;
                        }

                        &__divider {
                            color: ${color("common.ui4")};
                            font-style: italic;
                        }

                        &__leadingZero {
                            margin: 0;
                        }
                    }

                    &__button {
                        padding-right: 0;
                    }

                    &__clear-button {
                        margin-right: 8px;
                    }

                    &--disabled {
                        background: ${color("common.ui2")};
                        opacity: 0.33;
                        button,
                        input,
                        span {
                            cursor: not-allowed !important;
                        }
                        .react-datetime-picker__button {
                            ${IconWrapper} {
                                color: ${color("common.ui3")};
                            }
                        }
                    }
                }
                .react-datetime-picker__calendar--open,
                .react-datetime-picker__calendar--closed,
                .react-datetime-picker__clock--open,
                .react-datetime-picker__clock--closed {
                    position: absolute;
                }
                min-width: 162px;
            `,
            formField: css`
                width: 100%;
            `,
        },
    },
} as xBaseTheme["datePicker"];
